import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import _ from "lodash";

import theme from "theme";
import { BOX_MAX_WIDTH } from "utils/constants";
import { downloadFileWithFileName } from "utils/file";

import Box from "components/Box";
import Divider from "components/Divider";
import Image from "components/Image";
import Paper from "components/Paper";
import Stack from "components/Stack";
import Text from "components/Text";
import { usePageTitle } from "components/usePageTitle";

import { usePublicProformaPDFRetrieve } from "entities/Deal/publicSdk";
import AppendixAccordion from "entities/Memo/components/AppendixAccordion";
import ExpiredShareToken from "entities/Memo/components/ExpiredShareToken";
import PageHeader from "entities/Memo/components/PageHeader";
import MemoSectionWrapper from "entities/Memo/components/SectionWrapper";
import {
  usePublicDealDetails,
  usePublicMemoDealDetails,
  usePublicMemoPDFSignedURLPolling,
  useVerifyShareToken,
} from "entities/Memo/publicSdk";
import { IMemoSection } from "entities/Memo/sdk";

import "./shareableMemo.css";

const ShareableMemo = () => {
  const params = useParams<{
    token: string;
  }>();
  const token = params.token;

  const [searchParams, setSearchParams] = useSearchParams();

  const memoPDFId = useMemo(
    () =>
      searchParams.get("memoPDFId")
        ? Number(searchParams.get("memoPDFId"))
        : undefined,
    [searchParams]
  );

  const [isFetchingForSignedURLEnabled, setIsFetchingForSignedURLEnabled] =
    useState<boolean>(!_.isNil(memoPDFId));

  // First we have to verify the token
  const { isValidating, error: errorToken } = useVerifyShareToken({
    token,
  });

  const { data: proformaPDF } = usePublicProformaPDFRetrieve({ token });

  const { data: fileData } = usePublicMemoPDFSignedURLPolling({
    token,
    memoPDFId,
    enabled:
      isFetchingForSignedURLEnabled && !_.isNil(token) && !_.isNil(memoPDFId),
  });

  useEffect(() => {
    if (!_.isNil(fileData)) {
      setIsFetchingForSignedURLEnabled(false);

      downloadFileWithFileName({
        fileUrl: fileData.url,
        fileName: fileData.original_file_name,
      }).then(() => {
        searchParams.delete("memoPDFId");
        setSearchParams(searchParams);
      });
    }
  }, [fileData, searchParams, setSearchParams]);

  /**
   * If the token is expired (useVerifyShareToken is return the error)
   * we don't want to send requests for deal and memo
   */
  const { data: deal } = usePublicDealDetails({
    token: !isValidating && !errorToken ? token : undefined,
  });
  const { data: memo } = usePublicMemoDealDetails({
    token: !isValidating && !errorToken ? token : undefined,
  });

  usePageTitle(
    deal?.name && memo?.name
      ? `Builders Patch: ${deal.name} - ${memo?.name} - Memo`
      : "Builders Patch: Memo"
  );

  // If the token is expired we want to show the dialog with a message
  if (errorToken) {
    return <ExpiredShareToken />;
  } else if (!memo || !deal) {
    return null;
  }

  return (
    <Paper
      sx={{
        overflowX: "auto",
        padding: theme.spacing(0, 4, 1, 4),
        "@media print": {
          overflow: "hidden",
          padding: "0",
          flexGrow: 1,
          marginLeft: "0 !important",
          width: "100%",
        },
      }}
    >
      <Stack>
        <Box
          sx={{
            width: "100%",
            maxWidth: BOX_MAX_WIDTH,
            alignSelf: "center",
          }}
        >
          <Box sx={{ marginBottom: theme.spacing(2) }}>
            <PageHeader
              dealId={deal.id}
              memoId={memo.id}
              memoName={memo?.name}
              dealName={deal?.name}
              isShareableLink={true}
              token={token}
            />
          </Box>
          <>
            <Stack direction="row" justifyContent="space-between">
              <Stack>
                <Text variant="text4" sx={{ fontWeight: "bold" }}>
                  {deal.name}
                </Text>
                <Text variant="text4">{memo.name}</Text>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  data-testid="additional-header-info"
                >
                  {_.isEmpty(memo?.additional_header_info) ? (
                    <Text
                      variant="text4"
                      data-testid="additional-info-empty-value"
                    >
                      -- additional header info --
                    </Text>
                  ) : (
                    <Text variant="text4" data-testid="additional-info-value">
                      {memo.additional_header_info}
                    </Text>
                  )}
                </Stack>
              </Stack>
              {memo.company_logo && (
                <Image
                  sx={{
                    height: "56px",
                    maxWidth: "50%",
                    marginBottom: 0.25,
                  }}
                  src={memo.company_logo}
                />
              )}
            </Stack>
            <Divider sx={{ marginBottom: 2 }} />
            <Box
              sx={{
                "& > *": { marginBottom: 2 },
              }}
            >
              {memo.sections.map((section: IMemoSection) => (
                <MemoSectionWrapper
                  key={section.section_id}
                  section={section}
                  deal={deal}
                  externalToken={token}
                  onUpdate={() => {}}
                />
              ))}
              {memo.include_proforma_pdf && (
                <AppendixAccordion
                  proformaPDF={proformaPDF}
                  tooltip="This appendix was generated by printing out the underwriting pro forma."
                />
              )}
            </Box>
          </>
        </Box>
      </Stack>
    </Paper>
  );
};

export default ShareableMemo;
